import { useEffect, useState } from "react";
import { OfficerLayout } from "@/components/OfficerLayout";
import { StatutoryRegister, StatutoryTabs } from "@/components/StatutoryRegister";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { formatINR } from "@/lib/i18n";
import { BookOpenCheck, Plus, X, TrendingUp, TrendingDown, Wallet } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  "tax_collection", "fee_collection", "grant_received", "salary",
  "maintenance", "office_expense", "scheme_payment", "opening", "adjustment", "other",
];

export default function CashBook() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [data, setData] = useState({ entries: [], total_credit: 0, total_debit: 0, closing_balance: 0 });
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({
    panchayat_id: user?.panchayat_id || "",
    entry_date: new Date().toISOString().slice(0, 10),
    category: "other",
    description: "",
    debit: "",
    credit: "",
  });
  const [busy, setBusy] = useState(false);
  const [regTab, setRegTab] = useState("module");

  const load = () => {
    const q = {};
    if (user?.panchayat_id) q.panchayat_id = user.panchayat_id;
    api.get("/cashbook", { params: q }).then((r) => setData(r.data));
  };
  useEffect(() => { load(); }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/cashbook", {
        panchayat_id: form.panchayat_id || user?.panchayat_id,
        entry_date: form.entry_date,
        category: form.category,
        description: form.description,
        debit: parseFloat(form.debit) || 0,
        credit: parseFloat(form.credit) || 0,
      });
      toast.success("Entry added");
      setShow(false);
      setForm({ ...form, description: "", debit: "", credit: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <OfficerLayout>
      <div>
        <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#E87722]">{lang === "hi" ? "लेखा" : "Accounting"}</div>
            <h1 className="mt-2 font-heading text-3xl font-bold text-slate-900 flex items-center gap-2">
              <BookOpenCheck className="w-7 h-7 text-[#0A3161]" />
              {lang === "hi" ? "रोकड़ बही" : "Cash Book"}
            </h1>
            <p className="mt-1 text-sm text-slate-600">{lang === "hi" ? "तय-समय वसूली एवं व्ययों का दैनिक खाता।" : "Daily ledger of receipts and payments — auto-fed by approved tax collections."}</p>
          </div>
          {regTab === "module" && (
            <button onClick={() => setShow(true)} data-testid="cb-add-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-md text-white" style={{ background: "#0A3161" }}>
              <Plus className="w-4 h-4" /> {lang === "hi" ? "नई प्रविष्टि" : "New Entry"}
            </button>
          )}
        </div>

        <StatutoryTabs moduleLabel={lang === "hi" ? "रोजमेल" : "Rozmel"} codes={["I", "IX", "XVII"]} active={regTab} onChange={setRegTab} />
        {regTab !== "module" && <StatutoryRegister code={regTab} />}

        <div className={regTab === "module" ? "" : "hidden"}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
          <Tile icon={TrendingUp} label="Total Credit" value={formatINR(data.total_credit)} color="#107C10" tid="cb-credit" />
          <Tile icon={TrendingDown} label="Total Debit" value={formatINR(data.total_debit)} color="#E87722" tid="cb-debit" />
          <Tile icon={Wallet} label="Closing Balance" value={formatINR(data.closing_balance)} color="#0A3161" tid="cb-balance" />
        </div>

        <div className="rounded-md border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="cb-table">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              <tr>
                <th className="text-left px-3 py-3">Entry No.</th>
                <th className="text-left px-3 py-3">Date</th>
                <th className="text-left px-3 py-3">Category</th>
                <th className="text-left px-3 py-3">Description</th>
                <th className="text-right px-3 py-3">Debit</th>
                <th className="text-right px-3 py-3">Credit</th>
                <th className="text-right px-3 py-3">Balance</th>
              </tr>
            </thead>
            <tbody>
              {data.entries.map((e) => (
                <tr key={e.id} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-mono text-xs">{e.entry_no}</td>
                  <td className="px-3 py-2 text-xs">{e.entry_date}</td>
                  <td className="px-3 py-2 text-xs capitalize">{e.category.replace(/_/g, " ")}</td>
                  <td className="px-3 py-2">{e.description}</td>
                  <td className="px-3 py-2 text-right font-mono text-[#E87722]">{e.debit > 0 ? formatINR(e.debit) : "—"}</td>
                  <td className="px-3 py-2 text-right font-mono text-[#107C10]">{e.credit > 0 ? formatINR(e.credit) : "—"}</td>
                  <td className="px-3 py-2 text-right font-mono font-semibold">{formatINR(e.balance)}</td>
                </tr>
              ))}
              {data.entries.length === 0 && (
                <tr><td colSpan={7} className="py-10 text-center text-slate-400">No cash book entries yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
        </div>

        {show && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
            <div className="bg-white rounded-md w-full max-w-md">
              <div className="flex items-center justify-between p-5 border-b border-slate-200">
                <h2 className="font-heading text-xl font-bold">New Cash Book Entry</h2>
                <button onClick={() => setShow(false)} className="p-2 text-slate-400"><X className="w-5 h-5" /></button>
              </div>
              <form onSubmit={submit} className="p-5 space-y-3">
                <Field label="Date">
                  <input type="date" required value={form.entry_date} onChange={(e) => setForm({ ...form, entry_date: e.target.value })} className="form-input" data-testid="cb-date" />
                </Field>
                <Field label="Category">
                  <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="form-input" data-testid="cb-cat">
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
                  </select>
                </Field>
                <Field label="Description">
                  <input required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="form-input" data-testid="cb-desc" />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Debit (₹)">
                    <input type="number" step="0.01" value={form.debit} onChange={(e) => setForm({ ...form, debit: e.target.value, credit: "" })} className="form-input" data-testid="cb-debit-input" />
                  </Field>
                  <Field label="Credit (₹)">
                    <input type="number" step="0.01" value={form.credit} onChange={(e) => setForm({ ...form, credit: e.target.value, debit: "" })} className="form-input" data-testid="cb-credit-input" />
                  </Field>
                </div>
                <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                  <button type="button" onClick={() => setShow(false)} className="px-4 py-2 text-sm rounded-md border border-slate-300">Cancel</button>
                  <button type="submit" disabled={busy} data-testid="cb-submit" className="px-4 py-2 text-sm font-semibold rounded-md text-white" style={{ background: "#0A3161" }}>{busy ? "…" : "Save"}</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>

      <style>{`.form-input{width:100%;padding:0.55rem 0.75rem;border-radius:0.375rem;border:1px solid #CBD5E1;background:white;font-size:0.875rem;outline:none}.form-input:focus{border-color:#0A3161;box-shadow:0 0 0 3px rgba(10,49,97,0.08)}`}</style>
    </OfficerLayout>
  );
}

const Tile = ({ icon: Icon, label, value, color, tid }) => (
  <div className="rounded-md border border-slate-200 bg-white p-4 flex items-center gap-3" data-testid={tid}>
    <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: color + "15", color }}>
      <Icon className="w-5 h-5" />
    </div>
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">{label}</div>
      <div className="mt-0.5 font-heading text-xl font-bold text-slate-900">{value}</div>
    </div>
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);
